// TMOS User Provisioning Page
// Date: December 24, 2025
// Updated: December 26, 2025 - Added individual asset provisioning
// Purpose: Platform admin page for manually provisioning user assets

import React, { useState, useEffect } from 'react';
import { useLanguage } from '@/state/language/LanguageProvider';
import { useAuth } from '@/state/auth/AuthProvider';
import { FlexibleCRUDTable } from '@/components/common/FlexibleCRUDTable';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/Tabs';
import { userEntity } from '@/config/entities/users';
import { GenericCRUDService } from '@/services/genericCRUD';
import api from '@/services/api';
import { toast } from 'react-hot-toast';
import { ServerIcon, ArrowPathIcon, EyeIcon } from '@heroicons/react/24/outline';
import { provisioningProfileService, ProvisioningProfile } from '@/services/provisioningProfileService';

interface User {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  status: string;
  tenant_id?: string;
  tenant_name: string;
  is_tenant_admin: boolean;
  created_at: string;
}

interface Tenant {
  id: string;
  name: string;
  domain?: string;
  status: string;
}

interface Template {
  id: string;
  name: string;
  description: string | null;
  template_type: string;
  marketplace_visibility: string;
  is_universal: boolean;
}

const UserProvisioningPage: React.FC = () => {
  const { t } = useLanguage();
  const { user: currentUser } = useAuth();
  const [activeTab, setActiveTab] = useState('profile-based');
  
  // Profile-based provisioning state
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [showProvisionModal, setShowProvisionModal] = useState(false);
  const [provisioning, setProvisioning] = useState(false);
  const [profiles, setProfiles] = useState<ProvisioningProfile[]>([]);
  const [selectedProfileCode, setSelectedProfileCode] = useState<string>('');
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Individual asset provisioning state
  const [targetType, setTargetType] = useState<'USER' | 'TENANT'>('USER');
  const [selectedTargetUserId, setSelectedTargetUserId] = useState<string>('');
  const [selectedTargetTenantId, setSelectedTargetTenantId] = useState<string>('');
  const [availableUsers, setAvailableUsers] = useState<User[]>([]);
  const [availableTenants, setAvailableTenants] = useState<Tenant[]>([]);
  const [availableTemplates, setAvailableTemplates] = useState<Template[]>([]);
  const [selectedTemplateIds, setSelectedTemplateIds] = useState<string[]>([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [loadingTenants, setLoadingTenants] = useState(false);
  const [provisioningAssets, setProvisioningAssets] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewTemplate, setPreviewTemplate] = useState<Template | null>(null);

  const crudService = new GenericCRUDService(userEntity);
  const isPlatformAdmin = currentUser?.role === 'Platform Administrator';

  useEffect(() => {
    loadProfiles();
  }, []);

  useEffect(() => {
    if (activeTab === 'individual') {
      loadUsers();
      loadTenants();
      loadTemplates();
    }
  }, [activeTab, targetType]);

  const loadProfiles = async () => {
    try {
      const data = await provisioningProfileService.getAllProfiles();
      setProfiles(data);
    } catch (error: any) {
      console.error('Error loading provisioning profiles:', error);
      toast.error(error.message || t('userProvisioning.failedToLoadProfiles') || 'Failed to load provisioning profiles');
    }
  };

  const loadUsers = async () => {
    try {
      setLoadingUsers(true);
      const response = await api.get('/users/admin/users', {
        params: { limit: 1000 }
      });
      
      if (response.data.success) {
        const users = response.data.data?.users || response.data.data || [];
        // Backend already filters to active, non-deleted users
        // Only filter by tenant if not platform admin (backend handles status/deleted filtering)
        const filteredUsers = users.filter((u: any) => 
          isPlatformAdmin || !currentUser?.tenantId || u.tenant_id === currentUser.tenantId
        );
        setAvailableUsers(filteredUsers);
      }
    } catch (error: any) {
      console.error('Error loading users:', error);
      toast.error(error.message || 'Failed to load users');
    } finally {
      setLoadingUsers(false);
    }
  };

  const loadTenants = async () => {
    if (!isPlatformAdmin) {
      // Tenant admins can only provision to their own tenant
      if (currentUser?.tenantId) {
        try {
          const response = await api.get(`/tenants/${currentUser.tenantId}`);
          if (response.data.success) {
            setAvailableTenants([{
              id: currentUser.tenantId,
              name: response.data.data.name,
              domain: response.data.data.domain,
              status: response.data.data.status
            }]);
          }
        } catch (error) {
          console.error('Error loading tenant:', error);
        }
      }
      return;
    }

    try {
      setLoadingTenants(true);
      const response = await api.get('/tenants', {
        params: { limit: 1000, status: 'ACTIVE' }
      });
      
      if (response.data.success) {
        const tenants = response.data.data?.tenants || response.data.data || [];
        // Backend already filters to active, non-deleted tenants
        setAvailableTenants(tenants);
      }
    } catch (error: any) {
      console.error('Error loading tenants:', error);
      toast.error(error.message || 'Failed to load tenants');
    } finally {
      setLoadingTenants(false);
    }
  };

  const loadTemplates = async () => {
    try {
      setLoadingTemplates(true);
      
      // Determine template types based on target type
      const templateTypes = targetType === 'USER' 
        ? ['ROADMAP', 'RISK', 'TRANSFORMATION', 'ACTION']
        : ['HIERARCHY', 'ROLE', 'KPI'];
      
      const response = await api.get('/provisioning-profiles/templates/available', {
        params: {
          templateTypes: templateTypes.join(','),
          provisionScope: targetType
        }
      });
      
      if (response.data.success) {
        setAvailableTemplates(response.data.templates || []);
      }
    } catch (error: any) {
      console.error('Error loading templates:', error);
      toast.error(error.message || 'Failed to load templates');
    } finally {
      setLoadingTemplates(false);
    }
  };

  // Profile-based provisioning handlers
  const handleProvisionUser = (user: User) => {
    setSelectedUser(user);
    setSelectedProfileCode('');
    setShowProvisionModal(true);
  };

  const handleProvisionSubmit = async () => {
    if (!selectedUser) {
      toast.error(t('userProvisioning.noUserSelected') || 'No user selected');
      return;
    }

    if (!selectedProfileCode) {
      toast.error(t('userProvisioning.selectProfileError') || 'Please select a provisioning profile');
      return;
    }

    try {
      setProvisioning(true);
      await api.post('/provisioning-profiles/provision-user', {
        userId: selectedUser.id,
        profileCode: selectedProfileCode
      });

      toast.success(`${t('userProvisioning.assetsProvisionedSuccess') || 'Assets provisioned successfully for'} ${selectedUser.first_name} ${selectedUser.last_name}`);
      setShowProvisionModal(false);
      setSelectedUser(null);
      setSelectedProfileCode('');

      setRefreshTrigger(prev => prev + 1);
    } catch (error: any) {
      console.error('Error provisioning user:', error);
      toast.error(error.response?.data?.message || error.message || t('userProvisioning.failedToProvision') || 'Failed to provision user');
    } finally {
      setProvisioning(false);
    }
  };

  const closeProvisionModal = () => {
    setShowProvisionModal(false);
    setSelectedUser(null);
    setSelectedProfileCode('');
  };

  // Individual asset provisioning handlers
  const handleTemplateToggle = (templateId: string) => {
    setSelectedTemplateIds(prev => 
      prev.includes(templateId)
        ? prev.filter(id => id !== templateId)
        : [...prev, templateId]
    );
  };

  const handleSelectAllTemplates = () => {
    if (selectedTemplateIds.length === availableTemplates.length) {
      setSelectedTemplateIds([]);
    } else {
      setSelectedTemplateIds(availableTemplates.map(t => t.id));
    }
  };

  const handlePreviewTemplate = (template: Template) => {
    setPreviewTemplate(template);
    setShowPreviewModal(true);
  };

  const handleProvisionAssets = async () => {
    if (selectedTemplateIds.length === 0) {
      toast.error(t('userProvisioning.selectTemplatesError') || 'Please select at least one template');
      return;
    }

    if (targetType === 'USER' && !selectedTargetUserId) {
      toast.error(t('userProvisioning.selectUserError') || 'Please select a user');
      return;
    }

    if (targetType === 'TENANT' && !selectedTargetTenantId) {
      toast.error(t('userProvisioning.selectTenantError') || 'Please select a tenant');
      return;
    }

    try {
      setProvisioningAssets(true);
      const response = await api.post('/provisioning-profiles/provision-assets', {
        templateIds: selectedTemplateIds,
        targetType,
        targetUserId: targetType === 'USER' ? selectedTargetUserId : null,
        targetTenantId: targetType === 'TENANT' ? selectedTargetTenantId : null
      });

      if (response.data.success !== false) {
        const { summary, message, results } = response.data;
        
        // Use the detailed message from backend if available, otherwise build a summary
        if (message) {
          toast.success(message, { duration: 8000 }); // Show longer for detailed messages
        } else {
          toast.success(
            `${t('userProvisioning.assetsProvisionedSuccess') || 'Provisioning completed'}: ${summary.successful} successful, ${summary.failed} failed, ${summary.skipped} skipped`
          );
        }
        
        // Log detailed results for debugging
        if (results && results.length > 0) {
          results.forEach((result: any) => {
            if (result.status === 'SKIPPED' || result.status === 'FAILED' || (result.status === 'SUCCESS' && result.errorMessage)) {
              console.log(`[Provisioning Result] ${result.templateName}: ${result.status} - ${result.errorMessage || 'No message'}`);
            }
          });
        }
        
        // Reset selections
        setSelectedTemplateIds([]);
        setSelectedTargetUserId('');
        setSelectedTargetTenantId('');
      }
    } catch (error: any) {
      console.error('Error provisioning assets:', error);
      toast.error(error.response?.data?.message || error.message || t('userProvisioning.failedToProvision') || 'Failed to provision assets');
    } finally {
      setProvisioningAssets(false);
    }
  };

  // Group templates by type
  const templatesByType = availableTemplates.reduce((acc, template) => {
    if (!acc[template.template_type]) {
      acc[template.template_type] = [];
    }
    acc[template.template_type].push(template);
    return acc;
  }, {} as Record<string, Template[]>);

  const customActions = [
    {
      label: t('userProvisioning.provisionAssets') || 'Provision Assets',
      icon: ServerIcon,
      onClick: handleProvisionUser,
      variant: 'primary' as const
    }
  ];

  return (
    <div className="min-h-screen bg-secondary-background p-6">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-[var(--color-surface-text)] mb-2">
            {t('userProvisioning.title') || 'User Provisioning'}
          </h1>
          <p className="text-[var(--color-secondary-text)]">
            {t('userProvisioning.description') || 'Manually provision templates and assets to users based on their profile'}
          </p>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="profile-based">
              {t('userProvisioning.profileBased') || 'Profile-Based Provisioning'}
            </TabsTrigger>
            <TabsTrigger value="individual">
              {t('userProvisioning.individualAssets') || 'Individual Asset Provisioning'}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="profile-based">
            <Card>
              <div className="p-6">
                <FlexibleCRUDTable
                  key={refreshTrigger}
                  entity={userEntity}
                  service={crudService}
                  customActions={customActions}
                  searchPlaceholder={t('userProvisioning.searchUsers') || 'Search users...'}
                  showPagination={true}
                  showSearch={true}
                />
              </div>
            </Card>

            {/* Profile-Based Provision Modal */}
            <Modal
              isOpen={showProvisionModal}
              onClose={closeProvisionModal}
              title={`${t('userProvisioning.provisionAssetsFor') || 'Provision Assets for'} ${selectedUser?.first_name} ${selectedUser?.last_name}`}
              size="md"
            >
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-[var(--color-surface-text)] mb-2">
                    {t('userProvisioning.userInformation') || 'User Information'}
                  </label>
                  <div className="bg-[var(--color-surface-bg)] p-3 rounded border border-[var(--color-border)]">
                    <p className="text-sm text-[var(--color-surface-text)]">
                      <strong>{t('userProvisioning.email') || 'Email:'}</strong> {selectedUser?.email}
                    </p>
                    <p className="text-sm text-[var(--color-surface-text)]">
                      <strong>{t('userProvisioning.tenant') || 'Tenant:'}</strong> {selectedUser?.tenant_name}
                    </p>
                    <p className="text-sm text-[var(--color-surface-text)]">
                      <strong>{t('userProvisioning.status') || 'Status:'}</strong> {selectedUser?.status}
                    </p>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-[var(--color-surface-text)] mb-2">
                    {t('userProvisioning.provisioningProfile') || 'Provisioning Profile'}
                  </label>
                  <select
                    value={selectedProfileCode}
                    onChange={(e) => setSelectedProfileCode(e.target.value)}
                    className="w-full px-3 py-2 border border-[var(--color-border)] rounded-md bg-[var(--color-surface-bg)] text-[var(--color-surface-text)] focus:outline-none focus:ring-2 focus:ring-[var(--color-info)]"
                  >
                    <option value="">{t('userProvisioning.selectProfile') || 'Select a profile...'}</option>
                    {profiles
                      .filter(p => p.is_active)
                      .map((profile) => (
                        <option key={profile.id} value={profile.profile_code}>
                          {profile.profile_name} ({profile.profile_code})
                        </option>
                      ))}
                  </select>
                  <p className="text-xs text-[var(--color-secondary-text)] mt-1">
                    {t('userProvisioning.profileHelpText') || 'Select the provisioning profile to use. If not specified, the user\'s current role will be used.'}
                  </p>
                </div>

                <div className="bg-[var(--color-info)]/10 border border-[var(--color-info)]/20 rounded-md p-3">
                  <p className="text-sm text-[var(--color-info)]">
                    <strong>{t('common.note') || 'Note:'}</strong> {t('userProvisioning.note') || 'This will provision all USER-level templates associated with the selected profile. Existing assets will not be duplicated (idempotent provisioning).'}
                  </p>
                </div>

                <div className="flex justify-end space-x-3 pt-4 border-t border-[var(--color-border)]">
                  <Button variant="outline" onClick={closeProvisionModal} disabled={provisioning}>
                    {t('common.cancel') || 'Cancel'}
                  </Button>
                  <Button onClick={handleProvisionSubmit} disabled={provisioning || !selectedProfileCode}>
                    {provisioning ? (
                      <>
                        <ArrowPathIcon className="h-4 w-4 mr-2 animate-spin" />
                        {t('userProvisioning.provisioning') || 'Provisioning...'}
                      </>
                    ) : (
                      <>
                        <ServerIcon className="h-4 w-4 mr-2" />
                        {t('userProvisioning.provisionAssets') || 'Provision Assets'}
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </Modal>
          </TabsContent>

          <TabsContent value="individual">
            <Card>
              <div className="p-6 space-y-6">
                {/* Target Type Selection */}
                <div>
                  <label className="block text-sm font-medium text-[var(--color-surface-text)] mb-2">
                    {t('userProvisioning.targetType') || 'Target Type'}
                  </label>
                  <div className="flex gap-4">
                    <label className="flex items-center">
                      <input
                        type="radio"
                        value="USER"
                        checked={targetType === 'USER'}
                        onChange={(e) => {
                          setTargetType(e.target.value as 'USER');
                          setSelectedTargetUserId('');
                          setSelectedTemplateIds([]);
                        }}
                        className="mr-2"
                      />
                      <span className="text-[var(--color-surface-text)]">
                        {t('userProvisioning.userLevel') || 'User-Level Assets'}
                      </span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        value="TENANT"
                        checked={targetType === 'TENANT'}
                        onChange={(e) => {
                          setTargetType(e.target.value as 'TENANT');
                          setSelectedTargetTenantId('');
                          setSelectedTemplateIds([]);
                        }}
                        className="mr-2"
                      />
                      <span className="text-[var(--color-surface-text)]">
                        {t('userProvisioning.tenantLevel') || 'Tenant-Level Assets'}
                      </span>
                    </label>
                  </div>
                  <p className="text-xs text-[var(--color-secondary-text)] mt-1">
                    {targetType === 'USER' 
                      ? t('userProvisioning.userLevelHelp') || 'User-level assets: Roadmaps, Risks, Transformations, Actions'
                      : t('userProvisioning.tenantLevelHelp') || 'Tenant-level assets: Hierarchies, Roles, KPIs'
                    }
                  </p>
                </div>

                {/* Target Selection */}
                {targetType === 'USER' ? (
                  <div>
                    <label className="block text-sm font-medium text-[var(--color-surface-text)] mb-2">
                      {t('userProvisioning.selectUser') || 'Select User'}
                    </label>
                    <select
                      value={selectedTargetUserId}
                      onChange={(e) => setSelectedTargetUserId(e.target.value)}
                      className="w-full px-3 py-2 border border-[var(--color-border)] rounded-md bg-[var(--color-surface-bg)] text-[var(--color-surface-text)] focus:outline-none focus:ring-2 focus:ring-[var(--color-info)]"
                      disabled={loadingUsers}
                    >
                      <option value="">
                        {loadingUsers 
                          ? (t('common.loading') || 'Loading...')
                          : (t('userProvisioning.selectUserPlaceholder') || '-- Select a user --')
                        }
                      </option>
                      {availableUsers.map(user => (
                        <option key={user.id} value={user.id}>
                          {user.first_name} {user.last_name} ({user.email}) - {user.tenant_name}
                        </option>
                      ))}
                    </select>
                  </div>
                ) : (
                  <div>
                    <label className="block text-sm font-medium text-[var(--color-surface-text)] mb-2">
                      {t('userProvisioning.selectTenant') || 'Select Tenant'}
                    </label>
                    <select
                      value={selectedTargetTenantId}
                      onChange={(e) => setSelectedTargetTenantId(e.target.value)}
                      className="w-full px-3 py-2 border border-[var(--color-border)] rounded-md bg-[var(--color-surface-bg)] text-[var(--color-surface-text)] focus:outline-none focus:ring-2 focus:ring-[var(--color-info)]"
                      disabled={loadingTenants}
                    >
                      <option value="">
                        {loadingTenants 
                          ? (t('common.loading') || 'Loading...')
                          : (t('userProvisioning.selectTenantPlaceholder') || '-- Select a tenant --')
                        }
                      </option>
                      {availableTenants.map(tenant => (
                        <option key={tenant.id} value={tenant.id}>
                          {tenant.name} {tenant.domain ? `(${tenant.domain})` : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Template Selection */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-[var(--color-surface-text)]">
                      {t('userProvisioning.selectTemplates') || 'Select Templates'}
                    </label>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleSelectAllTemplates}
                      disabled={availableTemplates.length === 0}
                    >
                      {selectedTemplateIds.length === availableTemplates.length
                        ? (t('common.deselectAll') || 'Deselect All')
                        : (t('common.selectAll') || 'Select All')
                      }
                    </Button>
                  </div>
                  
                  {loadingTemplates ? (
                    <div className="text-center py-8 text-[var(--color-secondary-text)]">
                      <ArrowPathIcon className="h-6 w-6 mx-auto animate-spin mb-2" />
                      {t('common.loading') || 'Loading templates...'}
                    </div>
                  ) : availableTemplates.length === 0 ? (
                    <div className="text-center py-8 text-[var(--color-secondary-text)]">
                      {t('userProvisioning.noTemplatesAvailable') || 'No templates available'}
                    </div>
                  ) : (
                    <div className="space-y-4 max-h-96 overflow-y-auto border border-[var(--color-border)] rounded-md p-4">
                      {Object.entries(templatesByType).map(([type, templates]) => (
                        <div key={type} className="mb-4">
                          <h4 className="font-medium text-[var(--color-surface-text)] mb-2">
                            {type} ({templates.length})
                          </h4>
                          <div className="space-y-2">
                            {templates.map(template => (
                              <div
                                key={template.id}
                                className="flex items-start gap-3 p-2 rounded border border-[var(--color-border)] hover:bg-[var(--color-surface-bg)]/50"
                              >
                                <input
                                  type="checkbox"
                                  checked={selectedTemplateIds.includes(template.id)}
                                  onChange={() => handleTemplateToggle(template.id)}
                                  className="mt-1"
                                />
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2">
                                    <span className="font-medium text-[var(--color-surface-text)]">
                                      {template.name}
                                    </span>
                                    {template.is_universal && (
                                      <span className="text-xs bg-[var(--color-info)]/20 text-[var(--color-info)] px-2 py-0.5 rounded">
                                        Universal
                                      </span>
                                    )}
                                    {template.marketplace_visibility === 'PUBLIC' && (
                                      <span className="text-xs bg-[var(--color-success)]/20 text-[var(--color-success)] px-2 py-0.5 rounded">
                                        Public
                                      </span>
                                    )}
                                  </div>
                                  {template.description && (
                                    <p className="text-sm text-[var(--color-secondary-text)] mt-1">
                                      {template.description}
                                    </p>
                                  )}
                                </div>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handlePreviewTemplate(template)}
                                >
                                  <EyeIcon className="h-4 w-4" />
                                </Button>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {selectedTemplateIds.length > 0 && (
                    <p className="text-sm text-[var(--color-secondary-text)] mt-2">
                      {t('userProvisioning.selectedCount') || 'Selected'}: {selectedTemplateIds.length} {t('userProvisioning.templates') || 'templates'}
                    </p>
                  )}
                </div>

                {/* Provision Button */}
                <div className="flex justify-end pt-4 border-t border-[var(--color-border)]">
                  <Button
                    onClick={handleProvisionAssets}
                    disabled={provisioningAssets || selectedTemplateIds.length === 0 || (targetType === 'USER' && !selectedTargetUserId) || (targetType === 'TENANT' && !selectedTargetTenantId)}
                  >
                    {provisioningAssets ? (
                      <>
                        <ArrowPathIcon className="h-4 w-4 mr-2 animate-spin" />
                        {t('userProvisioning.provisioning') || 'Provisioning...'}
                      </>
                    ) : (
                      <>
                        <ServerIcon className="h-4 w-4 mr-2" />
                        {t('userProvisioning.provisionSelectedAssets') || 'Provision Selected Assets'}
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </Card>

            {/* Preview Modal */}
            <Modal
              isOpen={showPreviewModal}
              onClose={() => setShowPreviewModal(false)}
              title={previewTemplate?.name || 'Template Preview'}
              size="lg"
            >
              {previewTemplate && (
                <div className="space-y-4">
                  <div>
                    <h3 className="font-medium text-[var(--color-surface-text)] mb-2">
                      {t('userProvisioning.templateDetails') || 'Template Details'}
                    </h3>
                    <div className="bg-[var(--color-surface-bg)] p-4 rounded border border-[var(--color-border)]">
                      <p className="text-sm text-[var(--color-surface-text)] mb-2">
                        <strong>{t('userProvisioning.type') || 'Type:'}</strong> {previewTemplate.template_type}
                      </p>
                      <p className="text-sm text-[var(--color-surface-text)] mb-2">
                        <strong>{t('userProvisioning.visibility') || 'Visibility:'}</strong> {previewTemplate.marketplace_visibility}
                      </p>
                      {previewTemplate.description && (
                        <p className="text-sm text-[var(--color-surface-text)]">
                          <strong>{t('userProvisioning.description') || 'Description:'}</strong> {previewTemplate.description}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex justify-end">
                    <Button variant="outline" onClick={() => setShowPreviewModal(false)}>
                      {t('common.close') || 'Close'}
                    </Button>
                  </div>
                </div>
              )}
            </Modal>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default UserProvisioningPage;
